import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import {
  createCollection,
  createCollectionIndex,
  deleteCollection,
  deleteCollectionIndex,
  getAllCollections,
  getCollectionById,
  getCollectionIndexes,
  importCSVToCollection,
  importFileToCollection,
  importJSONToCollection,
  initializeChunkUpload,
  updateCollection,
  uploadChunk,
} from '@/api/collections';
import { chunkJsonData } from '@/utils/chunking';
import { createGzipBlob } from '@/utils/gzip';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';

const logger = createLogger(LOG_NAMESPACES.DATA);

// Notification keys for deduplication
const NOTIFICATION_KEYS = {
  VALIDATION_ERROR: 'validation-error',
  LARGE_FILE_INFO: 'large-file-info',
  UPLOAD_SUCCESS: 'upload-success',
  UPLOAD_ERROR: 'upload-error',
};

// Utility function to format file size (shared with ImportFileDropdown)
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

export const useCollections = (id: string | undefined) => {
  return useQuery({
    queryKey: ['collections', id],
    queryFn: () => getAllCollections(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useCollectionById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['collection', id],
    queryFn: async () => getCollectionById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useCreateCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCollection,
    onError: (err) => {
      notification.error({
        message: err.message || "We couldn't create the collection",
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      notification.success({
        message: 'Collection created successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export const useUpdateCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCollection,
    onError: (err) => {
      notification.error({
        message: err?.message || "We couldn't update the collection",
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['collections'] });
      queryClient.refetchQueries({ queryKey: ['collection'] });
      notification.success({
        message: 'Collection updated successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export const useDeleteCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCollection,
    onError: (err) => {
      notification.error({
        message: err?.message || 'Could not delete the collection.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });

      notification.success({
        message: 'Collection deleted successfully!',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export const useCreateIndex = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCollectionIndex,
    onError: () => {
      notification.error({
        message: "We couldn't create all the indexes.",
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['collectionIndexes'] });
      notification.success({
        message: 'Collection indexes created successfully',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export const useDeleteIndex = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCollectionIndex,
    onError: () => {
      notification.error({
        message: 'Could not delete indexes.',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
      queryClient.refetchQueries({
        queryKey: ['collectionIndexes'],
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['collectionIndexes'],
      });
      notification.success({
        message: 'Indexes deleted successfully!',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export const useCollectionIndexes = (collectionId: string | undefined) => {
  return useQuery({
    queryKey: ['collectionIndexes', collectionId],
    queryFn: () => getCollectionIndexes(collectionId),
    enabled: !!collectionId,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useImportFileToCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      datasetId: string;
      file: Blob;
      mimeType: string;
      fileName: string;
      collectionName: string;
      onProgress?: (progress: number) => void;
    }) => {
      notification.destroy();

      if (body.file.size > 10 * 1024 * 1024) {
        notification.info({
          key: NOTIFICATION_KEYS.LARGE_FILE_INFO,
          message: 'Large File Detected',
          description: `Processing ${formatFileSize(body.file.size)} file. This may take several minutes. Please do not close this window.`,
          duration: 0,
          placement: 'topRight',
        });
      }

      return importFileToCollection(body, body.onProgress);
    },
    onError: (err) => {
      notification.destroy(NOTIFICATION_KEYS.LARGE_FILE_INFO);

      const errorMessage = err?.message || 'Could not import file.';

      notification.error({
        key: NOTIFICATION_KEYS.UPLOAD_ERROR,
        message: 'Import Failed',
        description: `Upload failed: ${errorMessage}`,
        duration: 8,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_: unknown, variables) => {
      notification.destroy(NOTIFICATION_KEYS.LARGE_FILE_INFO);

      queryClient.invalidateQueries({ queryKey: ['collections'] });

      const fileSize = formatFileSize(variables.file.size);
      notification.success({
        key: NOTIFICATION_KEYS.UPLOAD_SUCCESS,
        message: 'Import Successful',
        description: `File "${variables.fileName}" (${fileSize}) has been imported successfully!`,
        duration: 4,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * @deprecated Use `useImportFileToCollection` instead.
 */
export const useImportCSVToCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { datasetId: string; headline: boolean; file: File; onProgress?: (progress: number) => void }) => {
      notification.destroy();

      if (body.file.size > 10 * 1024 * 1024) {
        notification.info({
          key: NOTIFICATION_KEYS.LARGE_FILE_INFO,
          message: 'Large File Detected',
          description: `Processing ${formatFileSize(body.file.size)} file. This may take several minutes. Please do not close this window.`,
          duration: 0,
          placement: 'topRight',
        });
      }

      return importCSVToCollection(body, body.onProgress);
    },
    onError: (err, variables) => {
      notification.destroy(NOTIFICATION_KEYS.LARGE_FILE_INFO);

      const errorMessage = err?.message || 'Could not import CSV to collection.';
      const fileSize = formatFileSize(variables.file.size);

      const notificationConfig = {
        key: NOTIFICATION_KEYS.UPLOAD_ERROR,
        placement: 'topRight' as const,
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      };

      if (errorMessage.toLowerCase().includes('json') && errorMessage.toLowerCase().includes('processing')) {
        notification.error({
          ...notificationConfig,
          message: 'Server Configuration Error',
          description: 'The backend is trying to process your CSV file as JSON. Please contact support to resolve this issue.',
          duration: 10,
        });
      } else if (errorMessage.toLowerCase().includes('failed to load csv file')) {
        notification.error({
          ...notificationConfig,
          message: 'CSV Processing Failed',
          description: 'Failed to process CSV file. Please check that your file is properly formatted and try again.',
          duration: 6,
        });
      } else if (errorMessage.toLowerCase().includes('bad request')) {
        notification.error({
          ...notificationConfig,
          message: 'Invalid File',
          description: 'Invalid file format or content. Please verify your CSV file and try again.',
          duration: 6,
        });
      } else if (errorMessage.toLowerCase().includes('timeout')) {
        notification.error({
          ...notificationConfig,
          message: 'Upload Timeout',
          description: `Upload timed out for ${fileSize} file. This may happen with very large files. Please try again or contact support.`,
          duration: 8,
        });
      } else if (errorMessage.toLowerCase().includes('524') || errorMessage.toLowerCase().includes('gateway timeout')) {
        notification.warning({
          ...notificationConfig,
          message: 'Gateway Timeout (524)',
          description: `Your ${fileSize} file is likely still being processed by the server. Please wait a few minutes and check if your data was imported successfully.`,
          duration: 15,
        });
      } else if (errorMessage.toLowerCase().includes('500')) {
        notification.error({
          ...notificationConfig,
          message: 'Server Error',
          description: 'Server error occurred. Please try again in a few minutes or contact support if the problem persists.',
          duration: 8,
        });
      } else {
        notification.error({
          ...notificationConfig,
          message: 'Upload Failed',
          description: `Upload failed: ${errorMessage}. Please try again or contact support if the problem persists.`,
          duration: 8,
        });
      }
    },
    onSuccess: (_: unknown, variables) => {
      notification.destroy(NOTIFICATION_KEYS.LARGE_FILE_INFO);

      queryClient.invalidateQueries({ queryKey: ['collections'] });

      const fileSize = formatFileSize(variables.file.size);
      notification.success({
        key: NOTIFICATION_KEYS.UPLOAD_SUCCESS,
        message: 'CSV Import Successful',
        description: `CSV file "${variables.file.name}" (${fileSize}) has been imported successfully!`,
        duration: 4,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

/**
 * @deprecated Use `useImportFileToCollection` instead.
 */
export const useImportJSONToCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { datasetId: string; file: File; onProgress?: (progress: number) => void }) => {
      notification.destroy();

      return importJSONToCollection(body, body.onProgress);
    },
    onError: (err, variables) => {
      const errorMessage = err?.message || 'Could not import JSON to collection.';
      const fileSize = formatFileSize(variables.file.size);

      notification.error({
        key: NOTIFICATION_KEYS.UPLOAD_ERROR,
        message: 'JSON Import Failed',
        description: `Failed to import ${fileSize} JSON file: ${errorMessage}`,
        duration: 6,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: (_: unknown, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });

      const fileSize = formatFileSize(variables.file.size);
      notification.success({
        key: NOTIFICATION_KEYS.UPLOAD_SUCCESS,
        message: 'JSON Import Successful',
        description: `JSON file "${variables.file.name}" (${fileSize}) has been imported successfully!`,
        duration: 4,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};

export type UploadPhase = 'preparing' | 'initializing' | 'uploading';

export interface ImportChunksParams {
  datasetId: string;
  collectionName: string;
  jsonData: unknown[];
  totalDocuments: number;
  onProgress?: (progress: number) => void;
  onPhaseChange?: (phase: UploadPhase) => void;
}

// Progress band allocation per phase:
//   preparing    0 → 20 %  (chunking + gzip compression)
//   initializing 20 → 25 % (POST init to backend)
//   uploading    25 → 100 % (serial chunk PATCH requests)
const PHASE_BANDS = {
  preparing: { start: 0, end: 20 },
  initializing: { start: 20, end: 25 },
  uploading: { start: 25, end: 100 },
} as const;

const mapToGlobal = (phase: keyof typeof PHASE_BANDS, local: number): number => {
  const { start, end } = PHASE_BANDS[phase];
  return Math.round(start + (local / 100) * (end - start));
};

export const useImportChunksToCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ImportChunksParams) => {
      notification.destroy();

      const { datasetId, collectionName, jsonData, totalDocuments, onProgress, onPhaseChange } = params;

      // ── Phase 1: preparing — chunk + gzip ──────────────────────────────────
      onPhaseChange?.('preparing');
      onProgress?.(PHASE_BANDS.preparing.start);

      const chunkingResult = await chunkJsonData(jsonData, undefined, (localPct) => {
        onProgress?.(mapToGlobal('preparing', localPct));
      });

      logger.debug(`Chunking result: ${chunkingResult.totalChunks} chunks, total size ${chunkingResult.totalCompressedSize} bytes`);

      if (chunkingResult.totalChunks === 0) {
        throw new Error('No data to import');
      }

      // ── Phase 2: initializing — POST init ──────────────────────────────────
      onPhaseChange?.('initializing');
      onProgress?.(PHASE_BANDS.initializing.start);

      const { collectionId } = await initializeChunkUpload({
        collectionName,
        datasetId,
        totalChunks: chunkingResult.totalChunks,
        totalDocuments,
      });

      logger.info(`Upload initialized — collection_id: ${collectionId}, totalChunks: ${chunkingResult.totalChunks}`);
      onProgress?.(PHASE_BANDS.initializing.end);

      // ── Phase 3: uploading — serial PATCH chunks ───────────────────────────
      onPhaseChange?.('uploading');

      for (let i = 0; i < chunkingResult.chunks.length; i++) {
        const chunk = chunkingResult.chunks[i];
        const chunkFile = createGzipBlob(chunk.compressedData, chunk.hash);

        logger.info(`Sending chunk ${i + 1}/${chunkingResult.totalChunks} — hash: ${chunk.hash}, file: ${chunkFile.name}`);

        await uploadChunk(
          {
            collectionId,
            chunk: chunkFile,
            chunkIndex: i,
            totalChunks: chunkingResult.totalChunks,
            hash: chunk.hash,
          },
          (chunkProgress) => {
            const localPct = ((i + chunkProgress / 100) / chunkingResult.totalChunks) * 100;
            onProgress?.(mapToGlobal('uploading', localPct));
          },
        );

        logger.info(`Chunk ${i + 1}/${chunkingResult.totalChunks} uploaded successfully — hash: ${chunk.hash}`);
      }

      logger.info(`All ${chunkingResult.totalChunks} chunks uploaded for collection_id: ${collectionId}`);

      return {
        collectionName,
        collectionId,
        totalChunks: chunkingResult.totalChunks,
      };
    },
    onError: (err) => {
      const errorMessage = err?.message || 'Could not import data to collection.';

      notification.error({
        key: NOTIFICATION_KEYS.UPLOAD_ERROR,
        message: 'Import Failed',
        description: errorMessage,
        duration: 6,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-error-notifications bold-notification-message',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });

      notification.success({
        key: NOTIFICATION_KEYS.UPLOAD_SUCCESS,
        message: 'Import Successful',
        description: 'Data has been imported successfully!',
        duration: 4,
        placement: 'topRight',
        pauseOnHover: true,
        className: 'custom-success-notifications bold-notification-message',
      });
    },
  });
};
